import { useEffect, useRef, useState } from "react";

/** Lightweight scroll-reveal: fades + slides children into view once.
 *  `from` controls the slide direction ("up" | "left" | "right" | "none").
 *  Dependency-free and respects the user's reduced-motion preference. */
export function Reveal({ children, delay = 0, from = "up", className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    const prefersReduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduce || typeof IntersectionObserver === "undefined") { setReduce(true); setShown(true); return; }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setShown(true); obs.disconnect(); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const hiddenTransform = { up: "translateY(26px)", left: "translateX(-36px)", right: "translateX(36px)", none: "none" }[from] || "none";

  return (
    <div
      ref={ref}
      className={className}
      style={reduce ? undefined : {
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : hiddenTransform,
        transition: "opacity 650ms cubic-bezier(0.22,1,0.36,1), transform 650ms cubic-bezier(0.22,1,0.36,1)",
        transitionDelay: `${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}
